import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  UsersThree, MagnifyingGlass, Star, LinkSimple, ArrowsClockwise,
  Briefcase, Phone, EnvelopeSimple, MapPin, UserPlus, Trash,
} from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const STAGE_LABEL = {
  cv_received: "CV recibido", screening: "Filtro CV", interview: "Entrevista",
  references: "Referencias", offer: "Oferta", hired: "Contratado", rejected: "Rechazado",
};
const STAGE_COLOR = {
  cv_received: "bg-slate-100 text-slate-700",
  screening: "bg-blue-100 text-blue-800",
  interview: "bg-indigo-100 text-indigo-800",
  references: "bg-purple-100 text-purple-800",
  offer: "bg-amber-100 text-amber-800",
  hired: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
};

function StarRow({ n }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} size={11} weight={s <= n ? "fill" : "regular"} className={s <= n ? "text-amber-500" : "text-slate-300"} />
      ))}
    </div>
  );
}

/**
 * Talent Pool — cross-vacancy candidate directory. Recruiters can search,
 * filter and reuse strong candidates (e.g., silver medalists) in future
 * openings without re-entering their data.
 */
export default function TalentPoolTab({ canManage }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [minRating, setMinRating] = useState(0);
  const [minExp, setMinExp] = useState(0);

  const [openVacancies, setOpenVacancies] = useState([]);
  const [reuseFor, setReuseFor] = useState(null);
  const [reuseTargetId, setReuseTargetId] = useState("");
  const [reuseResetStage, setReuseResetStage] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (stage) params.set("stage", stage);
      if (minRating) params.set("min_rating", String(minRating));
      if (minExp) params.set("min_experience", String(minExp));
      const { data } = await api.get(`/hr/talent-pool?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [q, stage, minRating, minExp]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/hr/vacancies").then((r) => setOpenVacancies(r.data || [])).catch(() => {});
  }, [reuseFor]);

  const openReuse = (candidate) => {
    // Use the most recent application id (first entry after sort) as source
    const app = candidate.applications?.[0];
    setReuseFor({ ...candidate, source_candidate_id: app?.candidate_id });
    setReuseTargetId("");
    setReuseResetStage(true);
  };

  const submitReuse = async () => {
    if (!reuseFor?.source_candidate_id || !reuseTargetId) return;
    try {
      await api.post(`/hr/candidates/${reuseFor.source_candidate_id}/reuse`, {
        target_vacancy_id: reuseTargetId,
        reset_stage: reuseResetStage,
      });
      toast.success("Candidato agregado a la vacante destino");
      setReuseFor(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const targetOptions = useMemo(() => {
    if (!reuseFor) return [];
    const usedIds = new Set(reuseFor.applications?.map((a) => a.vacancy_id) || []);
    return openVacancies
      .filter((v) => !usedIds.has(v.id) && v.status !== "closed")
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [openVacancies, reuseFor]);

  return (
    <div className="space-y-4" data-testid="talent-pool">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
        <div className="md:col-span-2">
          <Label className="overline-label">Buscar</Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="tp-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nombre, email, ubicación, notas…"
              className="pl-8"
            />
          </div>
        </div>
        <div>
          <Label className="overline-label">Etapa</Label>
          <Select value={stage || "all"} onValueChange={(v) => setStage(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="tp-stage"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Todas</SelectItem>
              {Object.entries(STAGE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline-label">Rating mínimo</Label>
          <Select value={String(minRating)} onValueChange={(v) => setMinRating(Number(v))}>
            <SelectTrigger data-testid="tp-rating"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {[0, 1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n === 0 ? "Cualquiera" : `${n}+ ★`}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline-label">Experiencia mín.</Label>
          <Input type="number" min={0} value={minExp} onChange={(e) => setMinExp(Number(e.target.value) || 0)} data-testid="tp-exp" />
        </div>
        <div>
          <p className="text-xs text-slate-500">
            <b className="text-slate-900" data-testid="tp-total">{total}</b> candidatos únicos
          </p>
        </div>
        {canManage && (
          <div className="flex justify-end">
            <ProspectDialog onCreated={load} />
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <p className="text-sm text-slate-400 italic text-center py-10">Cargando…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-slate-400" data-testid="tp-empty">
            <UsersThree size={40} weight="duotone" className="mx-auto mb-2" />
            <p className="text-sm">Sin candidatos que coincidan con los filtros.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Candidato</th>
                <th className="text-left px-3 py-2">Perfil</th>
                <th className="text-left px-3 py-2">Aplicaciones</th>
                <th className="text-left px-3 py-2">Rating</th>
                <th className="text-right px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.identity_key} data-testid={`tp-row-${it.identity_key}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                  <td className="px-3 py-2 align-top">
                    <p className="font-semibold text-slate-900">{it.name}</p>
                    <div className="text-[11px] text-slate-500 space-y-0.5 mt-0.5">
                      {it.email && <p className="flex items-center gap-1"><EnvelopeSimple size={10} /> {it.email}</p>}
                      {it.phone && <p className="flex items-center gap-1"><Phone size={10} /> {it.phone}</p>}
                      {it.location && <p className="flex items-center gap-1"><MapPin size={10} /> {it.location}</p>}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <p className="text-xs text-slate-700">{it.experience_years}a exp. · {it.source || "-"}</p>
                    {it.expected_salary > 0 && (
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Pretensión: {it.currency} {Number(it.expected_salary).toLocaleString()}
                      </p>
                    )}
                    {it.resume_url && (
                      <a href={it.resume_url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-700 hover:underline flex items-center gap-1 mt-0.5">
                        <LinkSimple size={10} /> CV
                      </a>
                    )}
                    {(it.applications || []).some((a) => !a.vacancy_id) && (
                      <span className="inline-block text-[9px] font-bold px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-800 mt-1" data-testid={`tp-prospect-${it.identity_key}`}>PROSPECTO</span>
                    )}
                    {it.hired_employee_id && (
                      <span className="inline-block text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 mt-1">CONTRATADO</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <ul className="space-y-1">
                      {(it.applications || []).map((a, i) => (
                        <li key={i} className="text-[11px] flex items-center gap-1.5">
                          <Briefcase size={10} className="text-slate-400" />
                          <span className="text-slate-700 truncate max-w-[180px]" title={a.vacancy_title}>
                            {a.vacancy_id ? a.vacancy_title : <em className="text-fuchsia-700">Prospecto sin vacante</em>}
                          </span>
                          <span className={"text-[9px] font-semibold px-1.5 py-0.5 rounded " + (STAGE_COLOR[a.stage] || "bg-slate-100 text-slate-600")}>
                            {STAGE_LABEL[a.stage] || a.stage}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <StarRow n={it.rating} />
                  </td>
                  <td className="px-3 py-2 align-top text-right">
                    {canManage && (
                      <Button
                        size="sm" variant="outline"
                        data-testid={`tp-reuse-${it.identity_key}`}
                        onClick={() => openReuse(it)}
                        className="gap-1 text-blue-900 border-blue-200 hover:bg-blue-50"
                      >
                        <ArrowsClockwise size={12} weight="bold" /> Reutilizar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {reuseFor && (
        <Dialog open={Boolean(reuseFor)} onOpenChange={(o) => { if (!o) setReuseFor(null); }}>
          <DialogContent className="bg-white max-w-lg" data-testid="tp-reuse-modal">
            <DialogHeader>
              <DialogTitle>Reutilizar candidato en otra vacante</DialogTitle>
              <DialogDescription>
                <b>{reuseFor.name}</b> — se copiará como nuevo candidato en la vacante destino.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="overline-label">Vacante destino</Label>
                <Select value={reuseTargetId} onValueChange={setReuseTargetId}>
                  <SelectTrigger data-testid="tp-reuse-target"><SelectValue placeholder="Selecciona una vacante…" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {targetOptions.length === 0 ? (
                      <SelectItem value="_empty" disabled>Sin vacantes elegibles</SelectItem>
                    ) : targetOptions.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.title} · {v.department || "-"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={reuseResetStage}
                  onChange={(e) => setReuseResetStage(e.target.checked)}
                  data-testid="tp-reuse-reset"
                />
                Reiniciar la etapa a &quot;CV recibido&quot; en la vacante destino
              </label>
            </div>
            <Button
              data-testid="tp-reuse-submit"
              onClick={submitReuse}
              disabled={!reuseTargetId || reuseTargetId === "_empty"}
              className="bg-blue-900 hover:bg-blue-800 w-full mt-2"
            >
              Agregar a la vacante
            </Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/** Diálogo para alta manual de un prospecto por parte del administrador.
 * Guarda al candidato con `vacancy_id=""` — queda en el pool y se puede
 * reutilizar luego en cualquier vacante. */
function ProspectDialog({ onCreated }) {
  const empty = {
    name: "", email: "", phone: "", source: "Alta manual (RRHH)",
    location: "", experience_years: 0, expected_salary: 0, currency: "MXN",
    resume_url: "", notes: "", rating: 0, interested_in: "",
  };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setBusy(true);
    try {
      await api.post("/hr/prospects", form);
      toast.success("Prospecto agregado al pool de talento");
      setForm(empty);
      setOpen(false);
      onCreated?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(empty); }}>
      <Button
        size="sm"
        onClick={() => setOpen(true)}
        className="gap-1.5 bg-fuchsia-700 hover:bg-fuchsia-800 text-white"
        data-testid="tp-new-prospect-btn"
      >
        <UserPlus size={14} weight="bold" /> Nuevo prospecto
      </Button>
      <DialogContent className="bg-white max-w-lg" data-testid="tp-prospect-modal">
        <DialogHeader>
          <DialogTitle>Agregar prospecto independiente</DialogTitle>
          <DialogDescription>
            Alta manual sin postulación al portal. Queda en el pool de talento y podrás asignarlo a una vacante existente.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="overline-label">Nombre completo <span className="text-red-500">*</span></Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tp-prospect-name" />
          </div>
          <div>
            <Label className="overline-label">Email</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="tp-prospect-email" />
          </div>
          <div>
            <Label className="overline-label">Teléfono</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="tp-prospect-phone" />
          </div>
          <div>
            <Label className="overline-label">Ubicación</Label>
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="tp-prospect-location" />
          </div>
          <div>
            <Label className="overline-label">Interesado en (puesto)</Label>
            <Input value={form.interested_in} onChange={(e) => setForm({ ...form, interested_in: e.target.value })} data-testid="tp-prospect-interested" placeholder="Perforista Senior…" />
          </div>
          <div>
            <Label className="overline-label">Experiencia (años)</Label>
            <Input type="number" min={0} value={form.experience_years}
                   onChange={(e) => setForm({ ...form, experience_years: Number(e.target.value) || 0 })} data-testid="tp-prospect-exp" />
          </div>
          <div>
            <Label className="overline-label">Pretensión salarial</Label>
            <div className="flex gap-1">
              <Input type="number" min={0} value={form.expected_salary}
                     onChange={(e) => setForm({ ...form, expected_salary: Number(e.target.value) || 0 })} data-testid="tp-prospect-salary" />
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger className="w-20" data-testid="tp-prospect-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {["MXN", "USD", "PEN", "CLP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="overline-label">Rating</Label>
            <Select value={String(form.rating)} onValueChange={(v) => setForm({ ...form, rating: Number(v) })}>
              <SelectTrigger data-testid="tp-prospect-rating"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {[0, 1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n === 0 ? "Sin calificar" : `${n} ★`}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline-label">Fuente</Label>
            <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="tp-prospect-source" placeholder="Recomendación, LinkedIn…" />
          </div>
          <div className="col-span-2">
            <Label className="overline-label">Link al CV (opcional)</Label>
            <Input value={form.resume_url} onChange={(e) => setForm({ ...form, resume_url: e.target.value })} data-testid="tp-prospect-cv" placeholder="https://…" />
          </div>
          <div className="col-span-2">
            <Label className="overline-label">Notas</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="tp-prospect-notes" />
          </div>
        </div>
        <Button onClick={submit} disabled={busy || !form.name.trim()}
                className="bg-blue-900 hover:bg-blue-800 w-full mt-2"
                data-testid="tp-prospect-submit">
          <UserPlus size={14} weight="bold" className="mr-1.5" />
          {busy ? "Guardando…" : "Agregar al pool"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
